import Logo from '../../components/svgs/logo';
import CraneImage from '../../assets/images/crane.jpg';
import BtBackground from '../bt-background/bt-background';
import { DOMAttributes, useEffect, useState }  from 'react';
import appState from '../../state/AppStateContainer';
import { ServiceAuth } from '../../services/service-auth';
import { AxiosError } from 'axios';
import BtColumn from '../bt-column/bt-column';
import BtGrid from '../bt-grid/bt-grid';
import BtRow from '../bt-row/bt-row';
import BtCenter from '../bt-center/bt-center';
import { useTranslation, Trans } from "react-i18next";
import { serviceOrganization } from '../../services/service-organization';
import * as Sentry from "@sentry/react";

type CustomElement<T> = Partial<T & DOMAttributes<T> & { children: any }>;

declare global {
  namespace JSX {
    interface IntrinsicElements {
      ['bt-center']: CustomElement<any>;
    }
  }
}

type AccessToken = {
  userData: any,
  organizationData: any
}

type Theme = any;

function decodeJwt(token: string) {
  const [, payload] = token.split(".");
  if (!payload) throw new Error("Invalid JWT");
  const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
  const json = Buffer.from(base64, "base64").toString("utf8");
  return JSON.parse(json);
}

const shouldCallHvdLogin = async (token: string,) => {
  const localStorageToken = localStorage.getItem('token');
  if (localStorageToken) {
    const tokenObj = JSON.parse(localStorageToken);
    if (tokenObj.email === decodeJwt(token).UserId) {
      try {
        const tokenExpired = await ServiceAuth.hasTokenExpired();
        if (!tokenExpired) {
          return false;
        }
      } catch (error) {
        if (error instanceof AxiosError) {
          if (error.response?.status !== 401 || error.response?.data.message !== 'token.expired') {
            console.error(error);
            Sentry.captureException(error);
          }
        }
        return true;
      }
    }
  }

  return true;
}

export default function PageHvdLogin() {
  const [t] = useTranslation('login', { keyPrefix: 'sso' } );
  const [error, setError] = useState('');
  const logoutLink = '';

  useEffect(() => {
    (async () => {
      const token = new URL(window.location.href).searchParams.get('token');
      const product = new URL(window.location.href).searchParams.get('product');
      const country = new URL(window.location.href).searchParams.get('country');
      const urlToRedirectParam = new URL(window.location.href).searchParams.get('urlToRedirect');
      const productId = new URL(window.location.href).searchParams.get('product-id');

      const handleRedirect = async (data?: {accessToken?: AccessToken, theme?: Theme, error?: string}) => {
        if (urlToRedirectParam && !productId) {
          setError('Missing product param');
        } else {
          let verifiedProductId;
          if (productId) {
            verifiedProductId = await serviceOrganization.verifyOnePlatformProductId(productId);
            appState.setProductId(verifiedProductId);
          }
          
          if (data?.accessToken) {
            await appState.setToken(data.accessToken, true);
          }
          appState.setTheme(data?.theme || null);
          let urlToRedirect;
          if (urlToRedirectParam) {
            if (data?.error && decodeURIComponent(urlToRedirectParam).includes('/published-requests-table')) {
              urlToRedirect = '/published-quotations';
            } else {
              urlToRedirect = decodeURIComponent(urlToRedirectParam);
            }
          } else {
            urlToRedirect = appState.loadString('urlToRedirect');
          }
          localStorage.removeItem('urlToRedirect');
          const uuid = appState.getOrCreateAnonymousUUID();
          if (urlToRedirect) {
            const url = new URL(urlToRedirect, window.location.origin);
            if (urlToRedirectParam && productId) {
              url.searchParams.set("uuid", uuid);
              url.searchParams.set("product-id", verifiedProductId);
            }

            window.location.href = url.toString();
          } else {
            window.location.href = '/';
          }
          return;
        }
      }

      if (token) {
        try {
          if (await shouldCallHvdLogin(token)) {
            const data = await ServiceAuth.finishHvdSignIn(token, product, country);
            if (data && !data.error) {
              await handleRedirect(data);
            } else {
              setError(data?.error || 'Unknown error');
            }
          } else {
            await handleRedirect();
          }
        } catch (e) {
          if (urlToRedirectParam && decodeURIComponent(urlToRedirectParam).includes('/published-requests-table')) {
            await handleRedirect({ error: 'unknown_error' });
          } else {
            const err = e as AxiosError;
            const data: any = err.response?.data;
            console.log({ e });
            setError(data?.error || 'Unknown error');
          }
        }
        appState.setState({loader: false});
      } else {
        const url = new URL(window.location.href);
        const btoa = window.btoa;
        const params = [];
        if (product) {
          params.push(`product=${product}`);
        }
        if (country) {
          params.push(`country=${country}`);
        }
        if (urlToRedirectParam) {
          params.push(`urlToRedirect=${urlToRedirectParam}`);
        }
        if (productId) {
          params.push(`product-id=${productId}`);
        }
        const queryString = '?' + params.join('&');
        const returnUrl = btoa(url.origin + `/hvdlogin${queryString.length > 1 ? queryString : ''}`);

        window.location.href = `https://accurator${appState.isTest() ? '.staging' : ''}.hvdhub.com/Login?title=${btoa('Accurator')}&returnUrl=${returnUrl}&country=${btoa('SE')}&language=sv`;
      }
    })();
  }, []);

  function renderSsoError(error: string) {
    if (error.startsWith('No organization')) {
      return <Trans t={t} i18nKey="errorMessage.noOrganization" values={{logoutLink}} components={{a: <a/>}} />;
    } else if (error.startsWith('No application role')) {
      return <Trans t={t} i18nKey="errorMessage.noApplicationRole" values={{logoutLink}} components={{a: <a/>}} />;
    } else if (error.startsWith('No code')) {
      return <Trans t={t} i18nKey="errorMessage.noCode" components={{a: <a/>}} />;
    } else if (error.startsWith('invalid_grant: 54005')) {
      return <Trans t={t} i18nKey="errorMessage.usedCode" components={{a: <a/>}} />;
    } else if (error.startsWith('invalid_grant: 70008')) {
      return <Trans t={t} i18nKey="errorMessage.expiredCode" components={{a: <a/>}} />;
    } else if (error.startsWith('invalid_grant: 9002313')) {
      return <Trans t={t} i18nKey="errorMessage.invalidCode" components={{a: <a/>}} />;
    } else {
      return <Trans t={t} i18nKey="errorMessage.unknown" values={{ logoutLink, error: JSON.stringify(error) }} components={{ a: <a/> }} />
    }
  }

  if (error) {
    return (
      <div className="page-login">
        <BtGrid>
          <BtRow>
            <BtColumn className="bt-w50 first-column bg-white">
              <BtCenter>
                <div className="app-login bt-w60">
                  <Logo size="194px" type="logoWithTitle" />
                  <h2>{t('pageTitle', 'Inloggningen misslyckades')}</h2>
                  {renderSsoError(error)}
                </div>
              </BtCenter>
            </BtColumn>
            <BtColumn className="second-column">
              <BtBackground className="bg-login bt-background-position-left" src={CraneImage} />
            </BtColumn>
          </BtRow>
        </BtGrid>
      </div>
    );
  } else {
    appState.setState({loader: true});
  }
}
